import { SIGN_IN_INITIAL_VALUE_KEYS, SIGN_UP_MODES } from '@clerk/shared/internal/clerk-js/constants';
import { RedirectUrls } from '@clerk/shared/internal/clerk-js/redirectUrls';
import { getTaskEndpoint } from '@clerk/shared/internal/clerk-js/sessionTasks';
import { buildURL } from '@clerk/shared/internal/clerk-js/url';
import { useClerk } from '@clerk/shared/react';
import type { DecorateUrl, SessionResource } from '@clerk/shared/types';
import { isAbsoluteUrl } from '@clerk/shared/url';
import { createContext, useContext, useMemo } from 'react';

import { buildRedirectUrl, MAGIC_LINK_VERIFY_PATH_ROUTE, SSO_CALLBACK_PATH_ROUTE } from '../../common/redirects';
import { useEnvironment, useOptions } from '../../contexts';
import type { ParsedQueryString } from '../../router';
import { useRouter } from '../../router';
import type { SignInCtx } from '../../types';
import { warnAboutPasswordInSignInOrUpFlow } from '../../utils/warnAboutPasswordInSignInOrUpFlow';
import { clerkWindowNavigate } from '../../utils/windowNavigate';
import { getInitialValuesFromQueryParams } from '../utils';

export type SignInContextType = Omit<SignInCtx, 'fallbackRedirectUrl' | 'forceRedirectUrl'> & {
  navigateAfterSignIn: () => any;
  queryParams: ParsedQueryString;
  signUpUrl: string;
  signInUrl: string;
  signUpContinueUrl: string;
  signUpProtectCheckUrl: string;
  authQueryString: string | null;
  afterSignUpUrl: string;
  afterSignInUrl: string;
  transferable: boolean;
  waitlistUrl: string;
  emailLinkRedirectUrl: string;
  ssoCallbackUrl: string;
  isCombinedFlow: boolean;
  signUpIfMissingEnabled: boolean;
  navigateOnSetActive: (opts: {
    session: SessionResource;
    redirectUrl: string;
    decorateUrl: DecorateUrl;
  }) => Promise<unknown>;
  taskUrl: string | null;
};

export const SignInContext = createContext<SignInCtx | null>(null);

export const useSignInContext = (): SignInContextType => {
  const context = useContext(SignInContext);
  const { navigate, basePath, startPath } = useRouter();
  const { displayConfig, userSettings } = useEnvironment();
  const { queryParams, queryString } = useRouter();
  const signUpMode = userSettings.signUp.mode;
  const options = useOptions();
  const clerk = useClerk();

  if (context === null || context.componentName !== 'SignIn') {
    throw new Error(`Clerk: useSignInContext called outside of the mounted SignIn component.`);
  }

  const isCombinedFlow =
    (signUpMode !== 'restricted' &&
      Boolean(!options.signUpUrl && options.signInUrl && !isAbsoluteUrl(options.signInUrl)) &&
      context.withSignUp !== false) ||
    context.withSignUp ||
    false;

  const { componentName, mode, ...ctx } = context;
  const initialValuesFromQueryParams = useMemo(
    () => getInitialValuesFromQueryParams(queryString, SIGN_IN_INITIAL_VALUE_KEYS),
    [],
  );

  const redirectUrls = new RedirectUrls(
    options,
    {
      ...ctx,
      signInFallbackRedirectUrl: ctx.signInFallbackRedirectUrl || ctx.fallbackRedirectUrl,
      signInForceRedirectUrl: ctx.signInForceRedirectUrl || ctx.forceRedirectUrl,
    },
    queryParams,
    mode,
  );

  delete ctx.fallbackRedirectUrl;
  delete ctx.forceRedirectUrl;

  const afterSignInUrl = clerk.buildUrlWithAuth(redirectUrls.getAfterSignInUrl());
  const afterSignUpUrl = clerk.buildUrlWithAuth(redirectUrls.getAfterSignUpUrl());

  const navigateAfterSignIn = () => navigate(afterSignInUrl);

  // The `ctx` object here refers to the SignIn component's props.
  // SignIn's own options won't have a `signInUrl` property, so we have to get the value
  // from the `path` prop instead, when the routing is set to 'path'.
  let signInUrl = (ctx.routing === 'path' && ctx.path) || options.signInUrl || displayConfig.signInUrl;
  let signUpUrl = isCombinedFlow
    ? (ctx.routing === 'path' && ctx.path) || options.signUpUrl || displayConfig.signUpUrl
    : ctx.signUpUrl || options.signUpUrl || displayConfig.signUpUrl;
  let waitlistUrl = ctx.waitlistUrl || options.waitlistUrl || displayConfig.waitlistUrl;

  const preservedParams = redirectUrls.getPreservedSearchParams();
  signInUrl = buildURL({ base: signInUrl, hashSearchParams: [queryParams, preservedParams] }, { stringify: true });
  signUpUrl = buildURL({ base: signUpUrl, hashSearchParams: [queryParams, preservedParams] }, { stringify: true });
  waitlistUrl = buildURL({ base: waitlistUrl, hashSearchParams: [queryParams, preservedParams] }, { stringify: true });

  const authQueryString = redirectUrls.toSearchParams().toString();

  // Callback routes owned by the SignIn tree are always SignIn-rooted — including the combined-flow
  // branches mounted at `create/sso-callback` and `create/verify` under the SignIn component
  // (packages/ui/src/components/SignIn/index.tsx). Anchor both callback URLs to signInUrl and let
  // the endpoint carry the combined-flow affordance, instead of juggling bases per flow/routing.
  const emailLinkRedirectUrl = buildRedirectUrl({
    routing: ctx.routing,
    baseUrl: signInUrl,
    authQueryString,
    path: ctx.path,
    endpoint: isCombinedFlow ? '/create' + MAGIC_LINK_VERIFY_PATH_ROUTE : MAGIC_LINK_VERIFY_PATH_ROUTE,
  });
  const ssoCallbackUrl = buildRedirectUrl({
    routing: ctx.routing,
    baseUrl: signInUrl,
    authQueryString,
    path: ctx.path,
    endpoint:
      isCombinedFlow && ctx.routing !== 'virtual' ? '/create' + SSO_CALLBACK_PATH_ROUTE : SSO_CALLBACK_PATH_ROUTE,
  });

  if (isCombinedFlow) {
    signUpUrl = buildURL(
      { base: signInUrl, hashPath: '/create', hashSearchParams: [queryParams, preservedParams] },
      { stringify: true },
    );
  }

  // Static preconditions of the sign-up-if-missing flow, shared by SignInStart (which requests
  // `signUpIfMissing` on sign-in create) and the factor-one cards (which handle the resulting
  // `transferable` verification status). Per-attempt conditions (identifier type, password use)
  // stay at the call sites.
  // `attackProtection` is optional-chained because it is introduced alongside this flow: an app
  // pinned to an older clerk-js runtime builds a `UserSettings` resource without the field.
  const signUpIfMissingEnabled =
    isCombinedFlow &&
    Boolean(userSettings.attackProtection?.enumeration_protection?.enabled) &&
    signUpMode === SIGN_UP_MODES.PUBLIC;

  if (clerk.instanceType === 'development') {
    warnAboutPasswordInSignInOrUpFlow({
      signUpIfMissingEnabled,
      passwordEnabled: userSettings.instanceIsPasswordBased,
    });
  }

  const signUpContinueUrl = buildURL({ base: signUpUrl, hashPath: '/continue' }, { stringify: true });
  // Built off `signUpUrl`, which is rewritten to `<signInUrl>#/create` in the combined flow, so this
  // resolves to the embedded `…/create/protect-check` route there and the standalone sign-up route
  // otherwise — keeping a Protect-gated sign-up inside whichever component is mounted.
  const signUpProtectCheckUrl = buildURL({ base: signUpUrl, hashPath: '/protect-check' }, { stringify: true });

  const navigateOnSetActive = async ({
    session,
    redirectUrl,
    decorateUrl,
  }: {
    session: SessionResource;
    redirectUrl: string;
    decorateUrl: DecorateUrl;
  }) => {
    const currentTask = session.currentTask;
    if (!currentTask) {
      // Use decorateUrl to enable Safari ITP cookie refresh when needed
      const decoratedUrl = decorateUrl(redirectUrl);

      // If decorateUrl modified the URL (Safari ITP fix), do a full page navigation
      // The touch endpoint URL will be an absolute URL starting with http:// or https://
      if (decoratedUrl !== redirectUrl && /^https?:\/\//.test(decoratedUrl)) {
        clerkWindowNavigate(clerk, decoratedUrl);
        return;
      }

      return navigate(redirectUrl);
    }

    const taskEndpoint = getTaskEndpoint(currentTask);
    const taskNavigationPath = isCombinedFlow ? '/create' + taskEndpoint : taskEndpoint;

    // Base path is required for virtual routing with start path
    // eg: to navigate from /sign-in/factor-one to /sign-in/tasks/choose-organization
    return navigate(`/${basePath + startPath + taskNavigationPath}`);
  };

  const taskUrl = clerk.session?.currentTask
    ? (clerk.__internal_getOption('taskUrls')?.[clerk.session?.currentTask.key] ??
      buildRedirectUrl({
        routing: ctx.routing,
        baseUrl: signInUrl,
        authQueryString,
        path: ctx.path,
        endpoint: isCombinedFlow
          ? '/create' + getTaskEndpoint(clerk.session?.currentTask)
          : getTaskEndpoint(clerk.session?.currentTask),
      }))
    : null;

  return {
    ...ctx,
    transferable: ctx.transferable ?? true,
    oauthFlow: ctx.oauthFlow || 'auto',
    componentName,
    signUpUrl,
    signInUrl,
    waitlistUrl,
    afterSignInUrl,
    afterSignUpUrl,
    emailLinkRedirectUrl,
    ssoCallbackUrl,
    navigateAfterSignIn,
    signUpContinueUrl,
    signUpProtectCheckUrl,
    queryParams,
    initialValues: { ...ctx.initialValues, ...initialValuesFromQueryParams },
    authQueryString,
    isCombinedFlow,
    signUpIfMissingEnabled,
    navigateOnSetActive,
    taskUrl,
  };
};
